/**
 * Fabbisogno normativo: panoramica RSA + dettaglio per mansione/area.
 */
import React, { useState } from "react";
import { useApi } from "../hooks/useApi";
import {
  Card,
  Badge,
  Spinner,
  ErrorBox,
  WarningList,
  StatCard,
  SimpleTable,
} from "../components/ui/basics";

function DettaglioRsa({ rsa }) {
  const { data, loading, error, reload } = useApi(
    `/fabbisogno/${encodeURIComponent(rsa)}`,
  );

  if (loading) return <Spinner />;
  if (error) return <ErrorBox message={error} onRetry={reload} />;
  if (!data || data.status !== "ok")
    return <ErrorBox message={`RSA non calcolabile: ${data?.status}`} />;

  const righe = [...data.requisiti, ...data.requisitiAziendali];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Occupazione" value={data.occupazione} unit="ospiti" />
        <StatCard
          label="Fabbisogno normativo"
          value={data.totaleOreSett}
          unit="h/sett"
        />
        <StatCard label="FTE richiesti" value={data.totaleFte} unit="FTE" />
        <StatCard
          label="Standard aziendale"
          value={data.totaleAziendaleOreSett}
          unit="h/sett"
        />
      </div>
      <WarningList warnings={data.warnings} />
      <Card
        title={`Dettaglio per ${data.tipologiastandard === "per_area" ? "area funzionale" : "mansione"}`}
        subtitle={`${data.regione} — ${data.normativa || "normativa regionale"} · contratto ${data.contratto?.nome}`}
      >
        <SimpleTable
          rowKey={(r) => r.targetType + r.target}
          columns={[
            { key: "targetName", label: "Voce" },
            {
              key: "targetType",
              label: "Origine",
              render: (r) => (
                <Badge
                  status={
                    r.targetType === "aziendale" ? "solo_copertura" : "default"
                  }
                >
                  {r.targetType === "aziendale" ? "Aziendale" : "Normativa"}
                </Badge>
              ),
            },
            {
              key: "oreSett",
              label: "Ore/sett",
              align: "right",
              render: (r) => r.oreSett.toLocaleString("it-IT"),
            },
            {
              key: "fte",
              label: "FTE",
              align: "right",
              render: (r) => r.fte?.toLocaleString("it-IT"),
            },
          ]}
          rows={righe}
        />
      </Card>
    </div>
  );
}

export default function FabbisognoPage() {
  const { data, loading, error, reload } = useApi("/fabbisogno");
  const [selected, setSelected] = useState(null);

  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold text-gray-800">Fabbisogno personale</h2>
      <p className="text-sm text-gray-500">
        Ore/settimana richieste dalla normativa regionale (+ standard aziendale)
        in base all'occupazione per profilo ospite.
      </p>

      {loading && <Spinner />}
      {error && <ErrorBox message={error} onRetry={reload} />}

      {data && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {data.results.map((r) => (
            <button
              key={r.rsa}
              onClick={() => setSelected(r.rsa === selected ? null : r.rsa)}
              className={`text-left bg-white rounded-lg border p-4 hover:shadow transition ${
                selected === r.rsa
                  ? "border-primary-500 ring-1 ring-primary-300"
                  : "border-gray-200"
              }`}
            >
              <div className="flex items-center justify-between">
                <p className="font-semibold text-gray-800">{r.rsa}</p>
                {r.status === "ok" ? (
                  r.warnings?.length ? (
                    <Badge status="attenzione">
                      {r.warnings.length} avvisi
                    </Badge>
                  ) : (
                    <Badge status="ok">OK</Badge>
                  )
                ) : (
                  <Badge status="critico">{r.status}</Badge>
                )}
              </div>
              {r.status === "ok" && (
                <>
                  <p className="text-xs text-gray-500 mt-1">
                    {r.regione} · {r.occupazione} ospiti
                  </p>
                  <p className="text-lg font-bold mt-1">
                    {r.totaleFte}{" "}
                    <span className="text-sm font-normal text-gray-500">
                      FTE richiesti
                    </span>
                  </p>
                  <p className="text-xs text-gray-400">
                    {r.totaleOreSett.toLocaleString("it-IT")} h/sett normativa +{" "}
                    {r.totaleAziendaleOreSett.toLocaleString("it-IT")} aziendale
                  </p>
                </>
              )}
            </button>
          ))}
        </div>
      )}

      {selected && <DettaglioRsa rsa={selected} />}
    </div>
  );
}
